import Axios, { type AxiosRequestConfig } from 'axios';
import { faSuitcaseRolling } from '@fortawesome/pro-regular-svg-icons';
import TripPriceClass from '../TripPriceClass';
import FlightBillHandler from './FlightBillHandler';
import VerifyResult from '@itinerary/VerifyResult';
import PassengerForm from '@itinerary/PassengerForm';
import {
    ItineraryVerifyException,
    VerificationStatus,
} from '@itinerary/Exceptions';
import {
    AuxiliaryServiceApplicabilityTypes,
    BaggageType,
    CabinClassRequest,
    FlightsApiApiFlightsGetBaggageInfoPost,
    FlightsApiApiFlightsReservePost,
    type ReserveResponse,
    type PriceDto,
    type ReserveFlightRequest,
} from '@clients/FlightApi';
import { FlightServiceException } from '@itinerary/trip/flight/FlightModuleTypes';
import {
    type PassengerCount,
    type PassengerFare,
    type OptionalService,
    type OptionalServiceItem,
} from '@itinerary/types';
import { BuyExtraFields, PassengerFields } from '@store/form';
import { DiscountType, type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';
import { PaxTypeTitles } from '@itinerary/trip/TripModuleTypes';
import { type SegmentOption } from '@itinerary/trip/types';
import { type FlightBaggageDetails } from '@itinerary/trip/flight/types';

export default class FlightPriceClass extends TripPriceClass {
    private readonly _netPassengerFares: PassengerFare[] = [];

    baggage = 0;
    baggageDetails: FlightBaggageDetails | null = null;

    isCharter = false;
    isHigherCapacity = false;

    hasBaggage = false;
    needsLoadBaggage = false;
    baggageLoadingStatus: 'None' | 'Loading' | 'Done' = 'None';

    override get priceOptions(): SegmentOption[] {
        if (!this.international || !this.baggage) {
            return [];
        }
        return [
            {
                title: `بار رایگان: ${this.baggageTypeText}`,
                icon: faSuitcaseRolling,
                loading: this.baggageLoadingStatus === 'Loading',
            },
        ];
    }

    priceClass = '';

    cabinClass: CabinClassRequest = CabinClassRequest.Unknown;
    CabinClassDisplayName: string | null = null;
    override capacity = 0;
    override get prePriceDiscountPercent() {
        const serviceType = toValue(this.servicesPreDiscountPercent);
        return this.international
            ? serviceType.InternationalFlight
            : serviceType.Flight;
    }

    uniqueId = '';

    override showCapacity = true;

    override capacityText = '';

    override loadableTermsUrl: string | null = null;

    override cancellationTerms: string | null = '';

    discountToken = '';

    proposalId = '';

    override title = '';

    baggageType: BaggageType = BaggageType.None;
    providerBaggageType?: string;

    get baggageTypeText() {
        if (this.hasBaggage && (this.providerBaggageType || this.baggage)) {
            return (
                this.providerBaggageType ||
                `${this.baggage} ${this.baggageType === BaggageType.Kg ? 'کیلوگرم' : 'بسته'}`
            );
        } else {
            return 'بدون بار رایگان';
        }
    }

    constructor(
        price?: PriceDto,
        public international?: boolean,
        private readonly paxCount?: PassengerCount
    ) {
        super();

        if (price == null) {
            return;
        }
        this.isOfficial = !!price.HasOfficialRatePrice;
        this.hasPriority = !!price.IsAgencyHasPriorityOnShowingFlight;
        this.uniqueId = price.UniqueId ?? '';
        this.isCharter = !!price.IsCharter;
        this.isHigherCapacity = !!price.IncludeHigherCapacity;
        this.baggage = price.Baggage ?? 0;
        this.cabinClass = price.CabinClass ?? this.cabinClass;
        this.CabinClassDisplayName = price.CabinClassDisplayName ?? null;
        this.baggageType = price.BaggageType ?? BaggageType.None;
        this.providerBaggageType = price.ProviderBaggageType || undefined;
        this.hasBaggage = price.Baggage === null || (price.Baggage ?? 0) > 0;
        this.priceClass = price.BookingClass ?? '';
        this.cabinClass = price.CabinClass ?? CabinClassRequest.Unknown;

        this._netPassengerFares =
            price.PassengerFares?.map(fare => ({
                price: fare.TotalFare ?? 0,
                discount: fare.Discount ?? 0,
                paxType:
                    fare.PaxType === 'ADL'
                        ? PaxType.Adult
                        : fare.PaxType === 'CHD'
                          ? PaxType.Child
                          : PaxType.Infant,
            })) || [];

        this.capacity = price.Capacity ?? 0;
        this.capacityText =
            this.capacity >= 9 ? '+9' : this.capacity.toString();
        this.cancellationTerms =
            price.FareRules?.replace(/ (\d)\)/g, '\n$1)') || null;
        this.discountToken = price.DiscountToken ?? '';
        this.proposalId = price.ProposalId ?? '';
        this.title = `${
            price.CabinClassDisplayName
        } (${this.cabinClass === 'All' ? '' : 'کلاس نرخی'} ${this.priceClass})`;
        this.needsLoadBaggage = !!price.NeedGetPrice;
        this.loadableTermsUrl = price.FareRulesUrl ?? '';
        if (this.isOfficial) {
            this.priceDescription = 'نرخ رسمی ایرلاین';
        }
    }

    override get descriptionItems() {
        return [
            this.baggage === 0
                ? 'این پرواز بار رایگان ندارد'
                : this.baggage
                  ? `میزان بار مجاز برای این پرواز ${this.baggageTypeText} می‌باشد.`
                  : '',
        ].filter(Boolean);
    }

    async loadBaggage() {
        if (this.baggageLoadingStatus === 'Done' || !this.needsLoadBaggage) {
            return;
        }
        this.baggageLoadingStatus = 'Loading';
        try {
            const { data } = await FlightsApiApiFlightsGetBaggageInfoPost({
                proposalId: this.proposalId,
            });
            if (data) {
                this.hasBaggage = !!data.HasBaggage;
                this.baggage = data.Baggage ?? 0;
                this.baggageDetails = {
                    baggage: [
                        { paxTitle: 'بزرگسال', ...data.AdultBaggage },
                        { paxTitle: 'کودک', ...data.ChildBaggage },
                        { paxTitle: 'نوزاد', ...data.InfantBaggage },
                    ].filter(baggage => baggage.Baggage),
                    cabin: [
                        { paxTitle: 'بزرگسال', ...data.AdultCabinBaggage },
                        { paxTitle: 'کودک', ...data.ChildCabinBaggage },
                        { paxTitle: 'نوزاد', ...data.InfantCabinBaggage },
                    ].filter(baggage => baggage.Baggage),
                };
                if (
                    !this.baggageDetails?.baggage.length &&
                    !this.baggageDetails.cabin.length
                ) {
                    this.baggageDetails = null;
                }
                this.priceClass = data.BookingClass ?? '';
                this.baggageType = data.BaggageType ?? BaggageType.None;
            }
        } catch (e) {
            reportException(e);
        } finally {
            this.baggageLoadingStatus = 'Done';
        }
    }

    override get totalPriceForCheck(): number | null {
        if (this.paxCount) {
            return (
                ((this.adultFare &&
                    this.adultFare.price + this.adultFare.discount) ||
                    0) *
                    this.paxCount.adultCount +
                ((this.childFare &&
                    this.childFare.price + this.childFare.discount) ||
                    0) *
                    this.paxCount.childCount +
                ((this.infantFare &&
                    this.infantFare.price + this.infantFare.discount) ||
                    0) *
                    this.paxCount.infantCount
            );
        }
        return null;
    }

    override get totalPayableForCheck(): number | null {
        if (this.paxCount) {
            return (
                (this.adultFare?.price || 0) * this.paxCount.adultCount +
                (this.childFare?.price || 0) * this.paxCount.childCount +
                (this.infantFare?.price || 0) * this.paxCount.infantCount
            );
        }
        return null;
    }

    override get singlePrice(): number {
        return this.singlePayable + (this.adultFare?.discount ?? 0);
    }

    override get netPrice(): number {
        return this.adultFare ? this.adultFare.price : 0;
    }

    override get prePricePayable() {
        return this.netPrice;
    }

    get passengerFares(): PassengerFare[] {
        return this._netPassengerFares?.map(fare => {
            const customDiscountAmount = fare.price - (fare.discount ?? 0);

            return {
                price: this.haveCustomDiscount
                    ? this.getPriceWithPreDiscount(customDiscountAmount)
                    : fare.price,
                discount: this.haveCustomDiscount
                    ? this.getPreDiscountAmount(customDiscountAmount)
                    : fare.discount ?? 0,
                paxType: fare.paxType,
            };
        });
    }

    private async reserve(
        request: ReserveFlightRequest,
        params: AxiosRequestConfig = {}
    ) {
        try {
            const { data } = await FlightsApiApiFlightsReservePost(
                {
                    reserveFlightRequest: request,
                },
                params
            );
            return data;
        } catch (e) {
            if (Axios.isAxiosError(e)) {
                throw new FlightServiceException(
                    e.response?.data?.Message,
                    e.response?.data?.ShowAndReturn
                );
            } else {
                throw new FlightServiceException('');
            }
        }
    }

    override createPassengerForms(reserveData: ReserveResponse): {
        adultForm: PassengerForm;
        childForm: PassengerForm;
        infantForm: PassengerForm;
        optionalServiceTokens: string[];
    } {
        const defaultFields =
            PassengerFields.FirstName |
            PassengerFields.LastName |
            PassengerFields.Gender |
            PassengerFields.Nationality |
            PassengerFields.BirthDate;

        let iranianExtraFields = PassengerFields.NationalCode;
        const foreignerExtraFields =
            PassengerFields.NationalityCode |
            PassengerFields.PassportExpiry |
            PassengerFields.PassportNumber;

        let buyExtraFields = BuyExtraFields.None;
        if (this.international) {
            iranianExtraFields |=
                PassengerFields.PassportExpiry | PassengerFields.PassportNumber;
        }

        const infantOptionalServices: OptionalService[] = [];
        const childOptionalServices: OptionalService[] = [];
        const adultOptionalServices: OptionalService[] = [];
        const optionalServiceTokens: string[] = [];
        if (
            reserveData?.Itinerary?.Segments?.some(s =>
                s.Legs?.some(l => l.AirlineAuxiliaryServices?.length)
            )
        ) {
            buyExtraFields |= BuyExtraFields.PackageOptionalService;
            const defaultOption: OptionalServiceItem = {
                id: 'none',
                label: 'بدون خدمات اضافه',
                price: 0,
            };
            reserveData?.Itinerary?.Segments?.forEach(seg => {
                seg.Legs?.forEach(leg => {
                    const title = `انتخاب خدمات ${leg.Origin} به ${leg.Destination}`;
                    const adultServices: OptionalServiceItem[] = [];
                    const childServices: OptionalServiceItem[] = [];
                    const infantServices: OptionalServiceItem[] = [];
                    leg.AirlineAuxiliaryServices?.forEach(aux => {
                        if (
                            aux.ApplicabilityType ===
                            AuxiliaryServiceApplicabilityTypes.PerPassenger
                        ) {
                            optionalServiceTokens.push(aux.Token ?? '');
                            adultServices.push({
                                id: aux.Token ?? '',
                                label: aux.PersianName ?? '',
                                price: aux.AdultAmount ?? 0,
                            });
                            childServices.push({
                                id: aux.Token ?? '',
                                label: aux.PersianName ?? '',
                                price: aux.ChildAmount ?? 0,
                            });
                            infantServices.push({
                                id: aux.Token ?? '',
                                label: aux.PersianName ?? '',
                                price: aux.InfantAmount ?? 0,
                            });
                        }
                    });
                    if (adultServices.length) {
                        adultOptionalServices.push({
                            title,
                            selectedService: defaultOption,
                            services: [defaultOption, ...adultServices],
                        });
                    }
                    if (childServices.length) {
                        childOptionalServices.push({
                            title,
                            selectedService: defaultOption,
                            services: [defaultOption, ...childServices],
                        });
                    }
                    if (infantServices.length) {
                        infantOptionalServices.push({
                            title,
                            selectedService: defaultOption,
                            services: [defaultOption, ...infantServices],
                        });
                    }
                });
            });
        }

        const adultForm: PassengerForm = new PassengerForm({
            title: PaxTypeTitles[PaxType.Adult],
            type: PaxType.Adult,
            defaultFields,
            iranianExtraFields,
            foreignerExtraFields,
            buyExtraFields,
            optionalServiceList: adultOptionalServices,
        });

        const childForm: PassengerForm = new PassengerForm({
            title: PaxTypeTitles[PaxType.Child],
            type: PaxType.Child,
            defaultFields,
            iranianExtraFields,
            foreignerExtraFields,
            buyExtraFields,
            optionalServiceList: childOptionalServices,
        });

        const infantForm: PassengerForm = new PassengerForm({
            title: PaxTypeTitles[PaxType.Infant],
            type: PaxType.Infant,
            defaultFields,
            iranianExtraFields,
            foreignerExtraFields,
            buyExtraFields,
            optionalServiceList: infantOptionalServices,
        });

        return {
            adultForm,
            childForm,
            infantForm,
            optionalServiceTokens,
        };
    }

    override remainingTimeCalculator(arg: { passengersCount: number }) {
        if (this.international) {
            return 15;
        }
        return super.remainingTimeCalculator(arg);
    }

    override async verify(
        count: {
            adultCount: number;
            childCount: number;
            infantCount: number;
        },
        billId: number | null
    ): Promise<VerifyResult> {
        try {
            const response = await this.reserve({
                AdultCount: count.adultCount,
                ChildCount: count.childCount,
                InfantCount: count.infantCount,
                CabinClass: this.cabinClass,
                DiscountToken: this.discountToken,
                ProposalId: this.proposalId,
                BillId: billId || undefined,
                Baggage: !!this.baggage,
            });

            const countMap: Record<string, number> = {
                ADL: count.adultCount,
                CHD: count.childCount,
                INF: count.infantCount,
            };

            let totalFare =
                response.Itinerary?.Prices?.reduce(
                    (acc, p) =>
                        (p.PassengerFares?.reduce(
                            (acc, f) =>
                                (f.TotalFare ?? 0) * countMap[f.PaxType || ''] +
                                acc,
                            0
                        ) || 0) + acc,
                    0
                ) || 0;
            let discount =
                response.Itinerary?.Prices?.reduce(
                    (acc, p) =>
                        (p.PassengerFares?.reduce(
                            (acc, f) =>
                                (f.Discount || 0) * countMap[f.PaxType || ''] +
                                acc,
                            0
                        ) || 0) + acc,
                    0
                ) || 0;
            const { haveCustomDiscount } = this;
            if (haveCustomDiscount) {
                const netPrice = totalFare - discount;
                totalFare = this.getPriceWithPreDiscount(netPrice);
                discount = this.getPreDiscountAmount(netPrice);
            }

            const { adultForm, childForm, infantForm, optionalServiceTokens } =
                this.createPassengerForms(response);

            return new VerifyResult(
                new FlightBillHandler(
                    response.Token ?? '',
                    optionalServiceTokens
                ),
                {
                    billCode: response.BillCode || undefined,
                    remainingTime: this.remainingTimeCalculator({
                        passengersCount:
                            count.adultCount +
                            count.childCount +
                            count.infantCount,
                    }),
                    payablePrice: totalFare,
                    totalPrice: totalFare + discount,
                    haveCustomDiscount,
                    priceEntries: [
                        {
                            prices:
                                response.Itinerary?.Prices?.[0].PassengerFares?.map(
                                    f => ({
                                        count: countMap[f.PaxType || ''],
                                        paxType:
                                            f.PaxType === 'ADL'
                                                ? PaxType.Adult
                                                : f.PaxType === 'CHD'
                                                  ? PaxType.Child
                                                  : PaxType.Infant,
                                        discount: f.Discount ?? 0,
                                        price:
                                            (f.TotalFare ?? 0) +
                                            (f.Discount ?? 0),
                                    })
                                ) || [],
                        },
                    ],
                    discount,
                    discountType: DiscountType.Special,
                    adultForm,
                    childForm,
                    infantForm,
                    paxList: [
                        ...Array(count.adultCount)
                            .fill(0)
                            .map(_ => adultForm.clone()),

                        ...Array(count.childCount)
                            .fill(0)
                            .map(_ => childForm.clone()),

                        ...Array(count.infantCount)
                            .fill(0)
                            .map(_ => infantForm.clone()),
                    ],
                    cancellationTerms:
                        response.Itinerary?.Prices?.[0].FareRules?.replace(
                            / (\d)\)/g,
                            '\n$1)'
                        ) || '',
                    billId: response.BillId,
                    needsDestinationMobile: this.international,
                    warningMessages:
                        response.ReserveNotes?.map(n => ({
                            message: n.Message || '',
                            type: 'unknown',
                        })) || [],
                    departureTime: dayConfig
                        .gregory(
                            response.Itinerary?.Segments?.[0].Legs?.[0].DepartureTime?.substring(
                                0,
                                19
                            )
                        )
                        .toDate(),
                    maxCapacity: response.Itinerary?.Prices?.[0].Capacity,
                    extraInfoFields: response.ExtraInfoField ?? [],
                }
            );
        } catch (ex) {
            if (ex instanceof FlightServiceException && ex.showAndReturn) {
                throw new ItineraryVerifyException(
                    VerificationStatus.ShowAndReturn,
                    ex.message
                );
            } else if (ex instanceof FlightServiceException) {
                throw new ItineraryVerifyException(
                    VerificationStatus.UnknownError,
                    ex.message
                );
            }
            throw new ItineraryVerifyException(
                VerificationStatus.UnknownError,
                (ex as any).response
            );
        }
    }

    /**
     * Loads the fares of different types of passengers of the flight
     */
    override loadPreInvoice(count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): Promise<InvoiceEntry[]> {
        const entries: InvoiceEntry[] = this.passengerFares
            .map(f => ({
                ...f,
                price: f.price,
                discount: f.discount,
                count:
                    f.paxType === PaxType.Adult
                        ? count.adultCount
                        : f.paxType === PaxType.Child
                          ? count.childCount
                          : count.infantCount,
            }))
            .filter(i => i.count > 0);
        return Promise.resolve(entries);
    }
}
